import { useMemo } from 'react';

import { ClientsListItemData } from 'api/types/data';
import { formatToDate } from 'helpers';
import { useConvertDateToUserTimezone } from 'hooks';

import { ClientsListItem } from '../../types';

import { useColumns } from './columns';

const getLastSessionDate = (
  client: ClientsListItemData,
  convertDateToUserTimezone: (date: Date) => Date
): string | undefined => {
  const clientLastSessionDate = client.lastSessionDate;
  const representativeLastSessionDate = client.representative?.lastSessionDate;

  let date: Date | undefined;

  if (clientLastSessionDate && representativeLastSessionDate) {
    date =
      clientLastSessionDate > representativeLastSessionDate
        ? clientLastSessionDate
        : representativeLastSessionDate;
  } else {
    date = clientLastSessionDate ?? representativeLastSessionDate;
  }

  if (!date) {
    return undefined;
  }

  return formatToDate(convertDateToUserTimezone(date));
};

const getPersonalAccountOpeningDate = (
  client: ClientsListItemData,
  convertDateToUserTimezone: (date: Date) => Date
): string | undefined => {
  const { personalAccountOpeningDate } = client;

  if (!personalAccountOpeningDate) {
    return undefined;
  }

  return formatToDate(convertDateToUserTimezone(personalAccountOpeningDate));
};

export const useClientsTable = (clients: ClientsListItemData[]) => {
  const { convertDateToUserTimezone } = useConvertDateToUserTimezone();

  const rows = useMemo(
    () =>
      clients.map((client) => ({
        ...client,
        lastSessionDate: getLastSessionDate(client, convertDateToUserTimezone),
        personalAccountOpeningDate: getPersonalAccountOpeningDate(
          client,
          convertDateToUserTimezone
        ),
      })) as ClientsListItem[],
    [clients, convertDateToUserTimezone]
  );

  const columns = useColumns();

  return { rows, columns };
};
